// Push Notification Manager
// Handles PWA push notification subscription, permissions, and communication
// with backend. Rewired for teliance-events: endpoints are tenant-scoped
// (getApiBase → /api/tenants/<slug>/event-dashboard) and go through apiFetch
// (Bearer auth); the service worker registers at root scope on the new origin.

import { apiFetch } from '@/lib/api.js';
import { getApiBase as dashApiBase } from './dashboardContext';

// Convert VAPID public key to Uint8Array for subscription
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Tenant-scoped API base for the dashboard's push endpoints.
function getApiBase() {
  return dashApiBase();
}

class PushNotificationManager {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.vapidPublicKey = null;
    this.onUpdateAvailableCallback = null;
    this.waitingWorker = null;
    this.updateCheckInterval = null;
    this.isActivatingUpdate = false;
    this.userRequestedUpdate = false; // Track if user explicitly clicked "Update Now"
    this.pageLoadTime = Date.now(); // Track when page loaded to detect fresh opens
    this.isCheckingForUpdate = false; // Mutex to prevent concurrent update checks
    this.lastVersionCheck = null; // Cache last version check result

    // Load lastReloadTime from localStorage (persists across page reloads)
    const storedLastReload = localStorage.getItem('pwa_lastReloadTime');
    this.lastReloadTime = storedLastReload ? parseInt(storedLastReload, 10) : 0;
  }

  // Check if push notifications are supported
  isSupported() {
    // Check basic browser support
    const hasBasicSupport = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

    // iOS Safari specific check
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;

    // iOS Safari only supports push in PWA mode (added to home screen)
    if (isIOS) {
      console.log('[Push] iOS detected - Push notifications only work when added to home screen');
      return hasBasicSupport && isStandalone;
    }

    return hasBasicSupport;
  }

  // Check if running on iOS Safari (not in PWA mode)
  isIOSSafariNotPWA() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isStandalone = window.navigator.standalone || window.matchMedia('(display-mode: standalone)').matches;
    return isIOS && !isStandalone;
  }

  // Get current notification permission status
  getPermissionStatus() {
    if (!this.isSupported()) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  // Check if currently subscribed
  async isSubscribed() {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      return subscription !== null;
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  // Register service worker
  async registerServiceWorker() {
    try {
      console.log('[Push] Registering service worker...');
      const registration = await navigator.serviceWorker.register('/service-worker.js', {
        scope: '/',
        updateViaCache: 'none' // Force bypass cache when checking for updates
      });

      console.log('[Push] Service worker registered:', registration);
      this.registration = registration;

      // Setup update detection listeners
      this.setupUpdateListeners(registration);

      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('[Push] Service worker is ready');

      // Manually check for updates
      console.log('[Push] Checking for service worker updates...');
      registration.update().catch(err => {
        console.log('[Push] Update check failed:', err);
      });

      // Start periodic update checks and visibility monitoring
      this.startPeriodicUpdateCheck();
      this.setupVisibilityListener();

      return registration;
    } catch (error) {
      console.error('[Push] Service worker registration failed:', error);
      throw error;
    }
  }

  // Extract version from service worker source code
  async getWorkerVersion(worker) {
    try {
      if (!worker || !worker.scriptURL) {
        console.warn('[Update] Cannot get version - invalid worker');
        return null;
      }

      // Fetch the service worker source with cache-busting
      const response = await fetch(`${worker.scriptURL}?t=${Date.now()}`, {
        cache: 'no-store'
      });

      if (!response.ok) {
        console.error('[Update] Failed to fetch worker source:', response.status);
        return null;
      }

      const source = await response.text();
      const versionMatch = source.match(/const VERSION = '([^']+)'/);

      if (versionMatch && versionMatch[1]) {
        return versionMatch[1];
      }

      console.warn('[Update] Could not parse VERSION from worker source');
      return null;
    } catch (error) {
      console.error('[Update] Error getting worker version:', error);
      return null;
    }
  }

  // Check if waiting worker has newer version than active worker
  async checkWaitingWorkerVersion(waitingWorker, activeWorker, isManualCheck = false) {
    try {
      console.log(`[Update] Checking waiting worker... (manual: ${isManualCheck})`);

      // No waiting worker - already on latest version
      if (!waitingWorker) {
        console.log(`[Update] ℹ️ No waiting worker found - already on latest version`);
        return false;
      }

      // No active worker - this is the first install
      if (!activeWorker) {
        console.log('[Update] No active worker - first install');
        return false;
      }

      // Actually compare VERSION constants from the service workers
      const [activeVersion, waitingVersion] = await Promise.all([
        this.getWorkerVersion(activeWorker),
        this.getWorkerVersion(waitingWorker)
      ]);

      console.log(`[Update] Active version: ${activeVersion}, Waiting version: ${waitingVersion}`);

      // If we can't get versions, fall back to browser's byte-by-byte detection
      if (!activeVersion || !waitingVersion) {
        console.log('[Update] Could not extract versions - trusting browser detection');
        // Only show update if browser detected a change
        const hasUpdate = waitingWorker.state === 'installed';
        if (hasUpdate) {
          this.waitingWorker = waitingWorker;
          this.notifyUpdateAvailable();
          return true;
        }
        return false;
      }

      // Parse versions for comparison (e.g., "1.0.34" -> [1, 0, 34])
      const parseVersion = (v) => v.split('.').map(n => parseInt(n, 10));
      const activeVersionParts = parseVersion(activeVersion);
      const waitingVersionParts = parseVersion(waitingVersion);

      // Compare version numbers
      let isNewer = false;
      for (let i = 0; i < Math.max(activeVersionParts.length, waitingVersionParts.length); i++) {
        const activePart = activeVersionParts[i] || 0;
        const waitingPart = waitingVersionParts[i] || 0;

        if (waitingPart > activePart) {
          isNewer = true;
          break;
        } else if (waitingPart < activePart) {
          // Waiting worker is OLDER - this shouldn't happen, clean it up
          console.warn(`[Update] ⚠️ Waiting worker (${waitingVersion}) is OLDER than active (${activeVersion}) - cleaning up`);
          this.cleanupStaleWorker(waitingWorker);
          return false;
        }
      }

      // Same version - no update needed
      if (!isNewer) {
        console.log(`[Update] Same version (${activeVersion}) - no update needed`);
        return false;
      }

      // New version detected!
      console.log(`[Update] ✅ New version available: ${activeVersion} -> ${waitingVersion}`);

      // For manual checks, show immediately
      if (isManualCheck) {
        console.log('[Update] Manual check - showing update immediately');
        this.waitingWorker = waitingWorker;
        this.notifyUpdateAvailable();
        return true;
      }

      // For automatic checks, apply minimal safeguards
      const timeSincePageLoad = Date.now() - this.pageLoadTime;
      if (timeSincePageLoad < 5000) {
        // Don't show within 5 seconds of page load
        console.log(`[Update] Deferring update notification (page loaded ${Math.round(timeSincePageLoad / 1000)}s ago)`);
        // Store for later but don't notify yet
        this.waitingWorker = waitingWorker;
        // Set a timeout to show it after the initial load period
        setTimeout(() => {
          if (this.waitingWorker === waitingWorker) {
            console.log('[Update] Showing deferred update notification');
            this.notifyUpdateAvailable();
          }
        }, 5000 - timeSincePageLoad);
        return true;
      }

      // Show the update notification
      this.waitingWorker = waitingWorker;
      this.notifyUpdateAvailable();
      return true;

    } catch (error) {
      console.error('[Update] Error checking worker versions:', error);
      return false;
    }
  }

  // Clean up stale/older service workers
  async cleanupStaleWorker(worker) {
    try {
      console.log('[Update] Attempting to clean up stale worker...');
      if (worker && worker.state === 'installed') {
        // Send skip waiting to move it through the lifecycle
        worker.postMessage({ type: 'SKIP_WAITING' });
        // The browser will handle the cleanup when it activates and finds it's not newer
      }
    } catch (error) {
      console.error('[Update] Failed to cleanup stale worker:', error);
    }
  }

  // Setup update detection listeners
  setupUpdateListeners(registration) {
    // Once-guard for the document/navigator-level listeners below —
    // registerServiceWorker() is called on mount AND on every push toggle;
    // without this each call stacked another controllerchange reload handler.
    if (this._updateListenersAttached) {
      if (registration.waiting) this.checkWaitingWorkerVersion(registration.waiting, registration.active);
      return;
    }
    this._updateListenersAttached = true;

    // Check for waiting service worker and compare versions
    if (registration.waiting) {
      console.log('[Update] Service worker update already waiting - checking versions...');

      // Compare versions asynchronously
      this.checkWaitingWorkerVersion(registration.waiting, registration.active);
    }

    // Always setup listeners for future updates
    // Listen for new service worker installing
    registration.addEventListener('updatefound', () => {
      console.log('[Update] New service worker found, installing...');
      const newWorker = registration.installing;

      newWorker.addEventListener('statechange', () => {
        console.log('[Update] Service worker state:', newWorker.state);

        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          // New service worker is installed and waiting - check version
          console.log('[Update] New service worker installed and waiting - checking versions...');
          this.checkWaitingWorkerVersion(newWorker, navigator.serviceWorker.controller);
        }
      });
    });

    // Listen for controller change (when new SW activates)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[Update] Service worker controller changed');

      // Calculate time since page load and last reload
      const timeSinceLoad = Date.now() - this.pageLoadTime;
      const timeSinceLastReload = Date.now() - this.lastReloadTime;

      // Prevent reload loops - don't reload if we just reloaded within last 5 seconds
      if (timeSinceLastReload < 5000) {
        console.log('[Update] ⚠️ Just reloaded recently, skipping to prevent loop');
        return;
      }

      // Always reload when controller changes to ensure new JavaScript code loads
      console.log('[Update] New service worker activated - reloading to get latest code');
      this.userRequestedUpdate = false;
      this.waitingWorker = null;
      this.updateBannerShown = false;

      // Track reload time to prevent loops
      this.lastReloadTime = Date.now();
      localStorage.setItem('pwa_lastReloadTime', this.lastReloadTime.toString());

      // Force fresh asset fetch with cache-busting URL parameter
      const baseUrl = window.location.href.split('?')[0];
      window.location.href = baseUrl + '?v=' + Date.now();
    });
  }

  // Notify callback that update is available
  notifyUpdateAvailable() {
    if (this.onUpdateAvailableCallback) {
      console.log('[Update] Notifying app of available update');
      this.onUpdateAvailableCallback();
    }
  }

  // Set callback for when update is available
  onUpdateAvailable(callback) {
    this.onUpdateAvailableCallback = callback;
  }

  // Manually check for updates (called by user clicking "Check for Updates")
  async checkForUpdates() {
    // Prevent concurrent checks
    if (this.isCheckingForUpdate) {
      console.log('[Update] Update check already in progress');
      return this.lastVersionCheck || { hasUpdate: false, isCheckingForUpdate: true };
    }

    this.isCheckingForUpdate = true;

    try {
      console.log('[Update] Manual update check initiated');

      // Get or register service worker
      const registration = this.registration || await navigator.serviceWorker.getRegistration('/');
      if (!registration) {
        console.log('[Update] No service worker registration found');
        this.isCheckingForUpdate = false;
        return { hasUpdate: false, error: 'No service worker registered' };
      }

      // Force update check from network
      console.log('[Update] Forcing update check from network...');
      await registration.update();

      // Small delay to allow update to process
      await new Promise(resolve => setTimeout(resolve, 100));

      // Check if there's a waiting worker with a newer version
      const hasUpdate = await this.checkWaitingWorkerVersion(
        registration.waiting,
        registration.active || navigator.serviceWorker.controller,
        true // isManualCheck
      );

      const result = {
        hasUpdate,
        currentVersion: null,
        newVersion: null
      };

      // Get version info for the result
      if (registration.active) {
        result.currentVersion = await this.getWorkerVersion(registration.active);
      }
      if (hasUpdate && registration.waiting) {
        result.newVersion = await this.getWorkerVersion(registration.waiting);
      }

      console.log('[Update] Manual check result:', result);
      this.lastVersionCheck = result;
      this.isCheckingForUpdate = false;
      return result;

    } catch (error) {
      console.error('[Update] Manual update check failed:', error);
      this.isCheckingForUpdate = false;
      return { hasUpdate: false, error: error.message };
    }
  }

  // Activate waiting service worker
  async activateUpdate() {
    try {
      console.log('[Update] Attempting to activate update...');

      // Mark that user explicitly requested this update
      this.userRequestedUpdate = true;

      // Get the latest waiting worker from registration
      const registration = this.registration || await navigator.serviceWorker.getRegistration('/');

      if (!registration) {
        console.error('[Update] No service worker registration found');
        this.userRequestedUpdate = false;
        return;
      }

      const waitingWorker = registration.waiting || this.waitingWorker;

      if (waitingWorker) {
        console.log('[Update] Found waiting worker, sending SKIP_WAITING message');
        console.log('[Update] Waiting worker state:', waitingWorker.state);

        // Clear the waiting worker reference immediately
        this.waitingWorker = null;

        waitingWorker.postMessage({ type: 'SKIP_WAITING' });

        // The page should reload via the controllerchange event
        // But if it doesn't happen within 3 seconds, force reload
        setTimeout(() => {
          console.log('[Update] Force reloading after timeout...');
          window.location.reload();
        }, 3000);
      } else {
        console.warn('[Update] No waiting service worker found');
        console.log('[Update] Registration state:', {
          active: registration.active?.state,
          waiting: registration.waiting?.state,
          installing: registration.installing?.state
        });
        this.userRequestedUpdate = false;
      }
    } catch (error) {
      console.error('[Update] Failed to activate update:', error);
      this.userRequestedUpdate = false;
    }
  }

  // Start periodic update checks (every 30 seconds)
  startPeriodicUpdateCheck() {
    // Clear any existing interval
    if (this.updateCheckInterval) {
      clearInterval(this.updateCheckInterval);
    }

    console.log('[Update] Starting periodic update checks (every 30 seconds)');
    this.updateCheckInterval = setInterval(() => {
      if (this.registration) {
        console.log('[Update] Periodic update check...');
        this.registration.update().catch(err => {
          console.log('[Update] Periodic check failed:', err);
        });
      }
    }, 30000); // Check every 30 seconds
  }

  // Setup visibility change listener to check for updates when tab becomes visible
  setupVisibilityListener() {
    if (this._visibilityListenerAttached) return; // once, see setupUpdateListeners
    this._visibilityListenerAttached = true;
    document.addEventListener('visibilitychange', async () => {
      if (!document.hidden && this.registration) {
        console.log('[Update] Tab became visible - checking for updates and activating if available...');

        // First check for updates
        await this.registration.update().catch(err => {
          console.log('[Update] Visibility check failed:', err);
        });

        // Then check if there's a waiting worker and activate it
        // (Safe to reload since app was just reopened)
        await this.checkAndActivateWaitingWorker();
      }
    });

    // Also check when window gains focus
    window.addEventListener('focus', async () => {
      if (this.registration) {
        console.log('[Update] Window focused - checking for updates and activating if available...');

        // Check for updates
        await this.registration.update().catch(err => {
          console.log('[Update] Focus check failed:', err);
        });

        // Check and activate waiting worker
        await this.checkAndActivateWaitingWorker();
      }
    });
  }

  // Check if there's a waiting worker and activate it (for visibility/focus handlers)
  async checkAndActivateWaitingWorker() {
    try {
      if (!this.registration) {
        return;
      }

      const registration = this.registration;

      // Check if there's a waiting worker
      if (registration.waiting && registration.active) {
        console.log('[Update] Found waiting service worker - checking if newer...');

        // Check if it's actually a newer version
        const hasUpdate = await this.checkWaitingWorkerVersion(
          registration.waiting,
          registration.active,
          false // Not a manual check, but should show banner if newer
        );

        if (hasUpdate) {
          console.log('[Update] Newer version available - showing update banner');
          // The checkWaitingWorkerVersion already showed the banner
          // User can click "Update Now" when ready
        } else {
          console.log('[Update] Waiting worker is not newer - no action needed');
        }
      }
    } catch (error) {
      console.error('[Update] Error checking/activating waiting worker:', error);
    }
  }

  // Request notification permission
  async requestPermission() {
    if (!this.isSupported()) {
      throw new Error('Push notifications are not supported in this browser');
    }

    console.log('[Push] Requesting notification permission...');
    const permission = await Notification.requestPermission();
    console.log('[Push] Permission result:', permission);

    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    return permission;
  }

  // Fetch VAPID public key from backend
  async fetchVapidPublicKey() {
    try {
      console.log('[Push] Fetching VAPID public key...');
      const response = await apiFetch(`${getApiBase()}/push/vapid-public-key`);

      if (!response.ok) {
        throw new Error(`Failed to fetch VAPID key: ${response.status}`);
      }

      const data = await response.json();
      this.vapidPublicKey = data.publicKey;
      console.log('[Push] VAPID public key fetched');

      return this.vapidPublicKey;
    } catch (error) {
      console.error('[Push] Failed to fetch VAPID key:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribe() {
    try {
      console.log('[Push] Starting subscription process...');

      // 1. Check if supported
      if (!this.isSupported()) {
        throw new Error('Push notifications are not supported');
      }

      // 2. Register service worker
      const registration = await this.registerServiceWorker();

      // 3. Request permission
      const permission = await this.requestPermission();
      if (permission !== 'granted') {
        throw new Error('Permission not granted');
      }

      // 4. Fetch VAPID public key
      const vapidPublicKey = await this.fetchVapidPublicKey();

      // 5. Subscribe to push notifications
      console.log('[Push] Creating push subscription...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      console.log('[Push] Push subscription created:', subscription);
      this.subscription = subscription;

      // 6. Send subscription to backend
      await this.sendSubscriptionToBackend(subscription);

      return subscription;
    } catch (error) {
      console.error('[Push] Subscription failed:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      console.log('[Push] Unsubscribing...');

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Remove from backend first
        await this.removeSubscriptionFromBackend(subscription);

        // Then unsubscribe locally
        await subscription.unsubscribe();
        console.log('[Push] Unsubscribed successfully');
        this.subscription = null;
      }

      return true;
    } catch (error) {
      console.error('[Push] Unsubscribe failed:', error);
      throw error;
    }
  }

  // Send subscription to backend
  async sendSubscriptionToBackend(subscription) {
    try {
      console.log('[Push] Sending subscription to backend...');

      const response = await apiFetch(`${getApiBase()}/push/subscribe`, {
        method: 'POST',
        body: JSON.stringify(subscription)
      });

      if (!response.ok) {
        throw new Error(`Failed to save subscription: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Push] Subscription saved to backend:', data);

      return data;
    } catch (error) {
      console.error('[Push] Failed to save subscription to backend:', error);
      throw error;
    }
  }

  // Remove subscription from backend
  async removeSubscriptionFromBackend(subscription) {
    try {
      console.log('[Push] Removing subscription from backend...');

      const response = await apiFetch(`${getApiBase()}/push/unsubscribe`, {
        method: 'POST',
        body: JSON.stringify(subscription)
      });

      if (!response.ok) {
        throw new Error(`Failed to remove subscription: ${response.status}`);
      }

      const data = await response.json();
      console.log('[Push] Subscription removed from backend:', data);

      return data;
    } catch (error) {
      console.error('[Push] Failed to remove subscription from backend:', error);
      throw error;
    }
  }

  // Listen for messages from service worker (notification clicks)
  setupMessageListener(callback) {
    // Swappable callback + single DOM listener: EventDashboardApp remounts on
    // tenant switch (key=tenantSlug) — re-registering must not stack handlers.
    this._messageCallback = callback;
    if (this._messageListenerAttached) return;
    this._messageListenerAttached = true;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        console.log('[Push] Message from service worker:', event.data);

        if (event.data && event.data.type === 'NOTIFICATION_CLICKED' && this._messageCallback) {
          this._messageCallback(event.data);
        }
      });
    }
  }
}

// Create a singleton instance
const pushNotificationManager = new PushNotificationManager();

export default pushNotificationManager;
